import { routes } from '@linqpal/models'
import { makeAutoObservable } from 'mobx'
import { PAGE_SIZE } from '../../../../ui/molecules/ApiTable'
import { CancellablePromise } from '@linqpal/models/src/helpers/caller'
import { PayablesQuotesStore } from './QuotesStore'
import { PayablesInvoicesStore } from './InvoicesStore'
import { paths } from '../../../links'
import { PayablesVendorsSort } from '@linqpal/models/src/dictionaries'
import { SortingOrder } from '@linqpal/models/src/dictionaries/global'

export interface IVendorsFilter {
  search?: string
}

export interface IVendor {
  id: string
  supplierName: string
  supplierPhone: string
  supplierIsInvited: boolean
  totalDueInvoices: number
  inHouseCreditIsEnabled: boolean
  arAdvanceIsEnabled: boolean
  totalDueSum: string
  totalPastDueInvoices: number
  totalPastDueSum: string
}

export interface IVendorsStatistics {
  totalDueInvoices: number
  totalDueSum: number
  availableCredit: number
  outstandingCredit: number
  creditLimit: number
}

interface ITableData {
  items: IVendor[]
  count: number
}

interface CurrentVendor {
  id: string
  supplierName: string
  supplierPhone: string
  supplierIsInvited: boolean
}

const defaultVendorStatistics = {
  totalDueSum: 0,
  totalDueInvoices: 0,
  availableCredit: 0,
  creditLimit: 0,
  outstandingCredit: 0,
}

export class VendorsStore {
  vendorId: string | null = null

  isMobile = false

  isFactoring = false

  initialRoute: string | null = null

  isLoading = false

  isStatisticsLoading = false

  dataLoaded = false

  request: CancellablePromise<any> | undefined

  vendorsLength = 0

  tableDisabled = false

  quotesPayables = new PayablesQuotesStore()

  invoicesPayables = new PayablesInvoicesStore()

  page = 1

  sortColumn = ''

  sortDirection = ''

  vendorsStatistics: IVendorsStatistics = defaultVendorStatistics

  tableData: ITableData = {
    items: [],
    count: 0,
  }

  filter: IVendorsFilter = {
    search: '',
  }

  currentVendor: CurrentVendor = {
    id: '',
    supplierName: '',
    supplierPhone: '',
    supplierIsInvited: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setVendor(id: string, name: string, phone: string, isInvited: boolean) {
    this.vendorId = id
    this.currentVendor = {
      id,
      supplierName: name,
      supplierPhone: phone,
      supplierIsInvited: isInvited,
    }

    this.quotesPayables.setVendorId(id)
    this.invoicesPayables.setVendor(id, isInvited)
  }

  setIsFactoring(inHouseCreditIsEnabled: boolean, arAdvanceIsEnabled: boolean) {
    const isFactoring = inHouseCreditIsEnabled && arAdvanceIsEnabled
    this.isFactoring = isFactoring
    this.invoicesPayables.setIsFactoring(isFactoring)
  }

  setIsMobile(isMobile: boolean) {
    this.isMobile = isMobile
  }

  resetVendor() {
    this.currentVendor = {
      id: '',
      supplierName: '',
      supplierPhone: '',
      supplierIsInvited: false,
    }
    this.vendorsStatistics = defaultVendorStatistics
  }

  resetSort() {
    this.sortColumn = PayablesVendorsSort.CREATED_AT
    this.sortDirection = SortingOrder.DESC
  }

  resetFilter() {
    this.filter = {
      search: '',
    }
    this.resetSort()
    this.page = 1
  }

  resetDataLoaded() {
    this.dataLoaded = false
    this.vendorsLength = 0
    this.initialRoute = null
    this.invoicesPayables.invalidate()
    this.quotesPayables.invalidate()
  }

  setPage(page: number) {
    this.page = page
    this.callApi()
  }

  enableTable() {
    this.tableDisabled = false
  }

  disableTable() {
    this.tableDisabled = true
  }

  setSort(sortColumn, sortDirection: string) {
    this.sortColumn = sortColumn
    this.sortDirection = sortDirection
    this.callApi()
  }

  setFilter(filter: IVendorsFilter) {
    this.filter = filter
    this.callApi()
  }

  callApi() {
    this.isLoading = true
    routes.invoices
      .vendorsList({
        ...this.filter,
        page: this.page,
        pageSize: PAGE_SIZE,
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection,
      })
      .then((data) => {
        if (!data.items.length && this.page !== 1) {
          this.setPage(1)
        } else {
          this.tableData = {
            items: data.items,
            count: data.count,
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() => (this.isLoading = false))
  }

  fetchStatisticsData() {
    this.isStatisticsLoading = true

    if (this.isFactoring) {
      routes.company
        .inHouseCreditInfo({ supplierId: this.vendorId })
        .then((data) => {
          this.vendorsStatistics.availableCredit = data.totalAvailableCredit
          this.vendorsStatistics.creditLimit = data.totalCreditLimit
          this.vendorsStatistics.outstandingCredit = data.totalOutstandingCredit
        })
        .catch((err) => console.log(err))
    }

    if (this.currentVendor) {
      routes.invoices
        .invoicesVendorsStatistics({
          supplierId: this.vendorId,
          supplierIsInvited: this.currentVendor.supplierIsInvited || undefined,
        })
        .then((data) => {
          this.vendorsStatistics.totalDueInvoices = data.totalDueInvoices || 0
          this.vendorsStatistics.totalDueSum = data.totalDueSum || 0
        })
        .catch((err) => console.log(err))
        .finally(() => (this.isStatisticsLoading = false))
    } else {
      this.vendorsStatistics.totalDueInvoices = 0
      this.vendorsStatistics.totalDueSum = 0

      this.isStatisticsLoading = false
    }
  }

  fetchData() {
    this.isLoading = true
    routes.invoices
      .vendorsList({
        ...this.filter,
        page: this.page,
        pageSize: PAGE_SIZE,
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection,
      })
      .then((data) => {
        this.vendorsLength = data.items.length
        if (this.vendorsLength === 1 && !this.isMobile) {
          this.initialRoute = paths.Console.Payables.Vendor
        } else {
          this.initialRoute = paths.Console.Payables.home
        }

        if (!data.items.length && this.page !== 1) {
          this.setPage(1)
        } else {
          this.tableData = {
            items: data.items,
            count: data.count,
          }
        }
      })
      .catch((err) => {
        this.initialRoute = paths.Console.Payables.home
        console.log(err)
      })
      .finally(() => {
        this.isLoading = false
        this.dataLoaded = true
      })
  }

  refreshData() {
    this.fetchStatisticsData()

    this.invoicesPayables.resetFilter()
    this.invoicesPayables.fetchData()
  }

  refreshQuotesData() {
    this.quotesPayables.resetFilter()
    this.quotesPayables.fetchData()
  }
}

export const Vendors = new VendorsStore()
export const SingleVendor = new VendorsStore()
